import { Route, Routes } from "react-router-dom";
import ErrorPage from "./pages/ErrorPage";
import SignInPage from "./pages/SignInPage";
import SignInPageAuto from "./pages/SignInPageAuto";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import HomePageOne from "./pages/HomePageOne";
import HomePageOneVolunteers from "./pages/HomePageOneVolunteers";
import TableDataPage from "./pages/TableDataPage";
import RouteScrollToTop from "./helper/RouteScrollToTop";
import ComplainForm from "./pages/ComplainForm";
import ComplainFormUpdate from "./pages/ComplainFormUpdate";
import ComplainFormStatusUpdate from "./pages/ComplainFormStatusUpdate";
import TableDataPageDfos from "./pages/TableDataPageDfos";
import TableDataPageVolunteers from "./pages/TableDataPageVolunteers";
import SignUpPage from "./pages/SignUpPage";
import ViewProfilePage from "./pages/ViewProfilePage";
import { ToastContainer } from 'react-toastify';
import ProtectedRoute from "./AuthContext/ProtectedRoute";
import React from "react";
import { useEffect } from "react";
import CustomRescuersForm from "./pages/CustomRescuersForm";
import CustomRescuersFormUpdate from "./pages/CustomRescuersFormUpdate";
import CustomRescuersTable from "./pages/CustomRescuersTable";
import UsersList from "./pages/UsersList";
import UserUpdatePage from "./pages/UserUpdatePage";
import DfoUpdatePage from "./pages/DfoUpdatePage";
import DfosForm from "./pages/DfosForm";
import ProtectedComponent from "./AuthContext/PermissionWrapper";
import TableDataPageDispatch from "./pages/TableDataPageDispatch";
import TableDataPageClosed from "./pages/TableDataPageClosed";
import TableDataPageReopen from "./pages/TableDataPageReopen";
import TableDataPageInformative from "./pages/TableDataPageInformative";
import ComplaintNaturePage from "./pages/ComplaintNaturePage";
import ComplainFormCloseUpdate from "./pages/ComplainFormCloseUpdate";
import ComplainFormDispatchUpdate from "./pages/ComplainFormDispatchUpdate";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AccessDeniedPage from "./pages/AccessDeniedPage";
import StandAloneComplainForm from "./pages/StandAloneComplainForm";
import ComplainantFeedbackFormPage from "./pages/ComplainantFeedbackFormPage";
import CustomUsersPage from "./pages/CustomUsersPage";

// PDF routes are code-split. @react-pdf/renderer is by far the heaviest
// dependency in the app; importing it eagerly put it in the main bundle, so
// every user downloaded and parsed it on first paint even when they never
// opened a report. Splitting these three routes keeps it out of main.js.
//
// NOTE: these must stay BELOW every static import — they are `const`
// declarations, and any `import` appearing after them trips eslint's
// `import/first` rule, which CRA treats as a build-breaking error in CI.
const ReportPerformance = React.lazy(() => import("./pages/ReportPerformance"));
const ComplainFormView = React.lazy(() => import("./pages/ComplainFormView"));
const EscalatedComplaintsPage = React.lazy(() => import("./pages/EscalatedComplaintsPage"));
const HierarchyOverviewPage = React.lazy(() => import("./pages/HierarchyOverviewPage"));

const FRONTEND_CACHE_VERSION = "2026-07-23-complaints-status-filter-v3";

function App() {
  useEffect(() => {
    const clearStaleFrontendCaches = async () => {
      if (!('caches' in window)) {
        return;
      }

      const storedVersion = localStorage.getItem('frontend-cache-version');
      if (storedVersion === FRONTEND_CACHE_VERSION) {
        return;
      }

      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
        localStorage.setItem('frontend-cache-version', FRONTEND_CACHE_VERSION);
      } catch (error) {
        console.log('Frontend cache cleanup failed: ', error);
      }
    };

    clearStaleFrontendCaches();

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/OneSignalSDKWorker.js', { updateViaCache: 'none' })
        .then((registration) => {
          registration.update();
          console.log('Service Worker Registered');
        })
        .catch(err => console.log('Service Worker Registration Failed: ', err));
    }
  }, []);
  return (
    <React.Fragment>
      <RouteScrollToTop />
      {/* Required by the React.lazy PDF routes above. The fallback is
          intentionally lightweight — the lazy chunk usually resolves in a
          few hundred ms, and each PDF page renders its own richer skeleton. */}
      <React.Suspense
        fallback={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "60vh",
              color: "#6b7280",
              gap: 10,
            }}
          >
            <span>Loading…</span>
          </div>
        }
      >
      <Routes>
        {/* Public Routes */}
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/auto-sign-in/:email/:password" element={<SignInPageAuto />} />
        <Route exact path="/access-denied" element={<AccessDeniedPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/public-form" element={<StandAloneComplainForm />} />
        <Route path="/feedback-form/:complaintId" element={<ComplainantFeedbackFormPage />} />
        <Route path="/" element={<SignInPage />} />
        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          {/* Complaints Routes */}
          <Route path="/complains/add" element={
            <ProtectedComponent module="complaints" action="create">
              <ComplainForm />
            </ProtectedComponent>
          } />
          <Route path="/complains/update/:id" element={
            <ProtectedComponent module="complaints" action="edit">
              <ComplainFormUpdate />
            </ProtectedComponent>
          } />
          <Route path="/complains/update-status/:id" element={
            <ProtectedComponent module="complaints" action="update_status">
              <ComplainFormStatusUpdate />
            </ProtectedComponent>
          } />
          <Route path="/complains/list" element={
            <ProtectedComponent module="complaints" action="view">
              <TableDataPage />
            </ProtectedComponent>
          } />
          <Route path="/informative-calls" element={
            <ProtectedComponent module="complaints" action="view">
              <TableDataPageInformative />
            </ProtectedComponent>
          } />
          <Route path="/escalated-complaints" element={
            <ProtectedComponent module="complaints" action="view">
              <EscalatedComplaintsPage />
            </ProtectedComponent>
          } />
          <Route path="/hierarchy-overview" element={
            <ProtectedComponent module="complaints" action="view">
              <HierarchyOverviewPage />
            </ProtectedComponent>
          } />
          <Route path="/complains/view/:id" element={
            <ProtectedComponent module="complaints" action="view">
              <ComplainFormView />
            </ProtectedComponent>
          } />

          <Route path="/dispatch/list" element={
            <ProtectedComponent module="lead_dispatch" action="view">
              <TableDataPageDispatch />
            </ProtectedComponent>
          } />
          <Route path="/closed/list" element={
            <ProtectedComponent module="lead_dispatch" action="view">
              <TableDataPageClosed />
            </ProtectedComponent>
          } />
          <Route path="/reopen/list" element={
            <ProtectedComponent module="lead_dispatch" action="view">
              <TableDataPageReopen />
            </ProtectedComponent>
          } />
          <Route path="/complaint-natures" element={
            <ProtectedComponent module="complaint_nature" action="view">
              <ComplaintNaturePage />
            </ProtectedComponent>
          } />
          <Route path="/dispatch/update/:id" element={
            <ProtectedComponent module="lead_dispatch" action="dispatch">
              <ComplainFormDispatchUpdate />
            </ProtectedComponent>
          } />
          <Route path="/closed/update/:id" element={
            <ProtectedComponent module="lead_dispatch" action="dispatch">
              <ComplainFormCloseUpdate />
            </ProtectedComponent>
          } />
          {/* Users Routes */}
          <Route path="/users/add" element={
            <ProtectedComponent module="users" action="create">
              <SignUpPage />
            </ProtectedComponent>
          } />
          <Route path="/users/update/:id" element={
            <ProtectedComponent module="users" action="edit">
              <UserUpdatePage />
            </ProtectedComponent>
          } />
          <Route path="/users/list" element={
            <ProtectedComponent module="users" action="view">
              <UsersList />
            </ProtectedComponent>
          } />
          <Route path="/custom-users" element={
            <ProtectedComponent module="custom_users" action="view">
              <CustomUsersPage />
            </ProtectedComponent>
          } />

          {/* Dashboard */}
          <Route path="/dashboard" element={
            <ProtectedComponent module="dashboard" action="view">
              <HomePageOne />
            </ProtectedComponent>
          } />
          <Route path="/dashboard-volunteers" element={
            <ProtectedComponent module="dashboard-volunteer" action="view">
              <HomePageOneVolunteers />
            </ProtectedComponent>
          } />

          {/* DFOs List */}
          <Route path="/dfos-list" element={
            <ProtectedComponent module="dfo_list" action="view">
              <TableDataPageDfos />
            </ProtectedComponent>
          } />
          {/* DFOs List */}
          <Route path="/volunteer-list" element={
            <ProtectedComponent module="dashboard-volunteer" action="view">
              <TableDataPageVolunteers />
            </ProtectedComponent>
          } />
          <Route path="/dfos/update/:id" element={
            <ProtectedComponent module="dfo_list" action="edit">
              <DfoUpdatePage />
            </ProtectedComponent>
          } />
          {/* Add New DCF (Directory) */}
          <Route path="/dfo-form" element={
            <ProtectedComponent module="dfo_list" action="create">
              <DfosForm />
            </ProtectedComponent>
          } />
          {/* Rescuers Routes */}
          <Route path="/rescures/add" element={
            <ProtectedComponent module="rescuers" action="create">
              <CustomRescuersForm />
            </ProtectedComponent>
          } />
          <Route path="/rescuers/update/:id" element={
            <ProtectedComponent module="rescuers" action="edit">
              <CustomRescuersFormUpdate />
            </ProtectedComponent>
          } />
          <Route path="/rescures/list" element={
            <ProtectedComponent module="rescuers" action="view">
              <CustomRescuersTable />
            </ProtectedComponent>
          } />

          {/* Reports Routes */}
          <Route path="/reports/performance" element={
            <ProtectedComponent module="reports" action="view">
              <ReportPerformance />
            </ProtectedComponent>
          } />
          {/* The escalated complaints report is now opened in a modal directly
              from the Escalated card on the dashboard (see UnitCountOne ->
              EscalatedReportModal), so the standalone route has been removed. */}

          {/* Common Routes */}
          <Route path="/change-password" element={<ViewProfilePage />} />
        </Route>

        {/* Catch-All Route */}
        <Route path="*" element={<ErrorPage />} />
      </Routes>
      </React.Suspense>

      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} closeOnClick pauseOnHover draggable />
    </React.Fragment>
  );
}

export default App;
